import React from 'react';
import {
  Box, Card, CardMedia, Chip, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  ShoppingCart as ShoppingCartIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';

import { fileUrl } from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * La vue GALERIE des produits — le pendant visuel du tableau (lot RU3-e).
 *
 * ── POURQUOI UNE GALERIE, ET POURQUOI ELLE NE REMPLACE PAS LE TABLEAU ───────
 * 🔴 LES DEUX VUES NE RÉPONDENT PAS À LA MÊME QUESTION. Le tableau répond à
 * « **combien**, et **lesquels sont bas** » — il aligne des nombres, donc il se
 * balaie en colonne. La galerie répond à « **c'est lequel** » — un opérateur
 * qui connaît un article par son aspect, pas par sa référence, le trouve d'un
 * regard là où il devrait lire quinze lignes. *Un basculement, pas un
 * remplacement : l'écran garde ses deux réponses.*
 *
 * ── CE QUE LA GALERIE MONTRE, ET CE QU'ELLE REFUSE DE MONTRER ───────────────
 * ⚠️ **PAS DE SUPPRESSION.** Les trois actions au survol sont **ajuster**,
 * **commander**, **modifier**. La ligne du tableau en porte une quatrième —
 * supprimer — et elle n'entre pas ici : *une vignette se survole par dizaines
 * en balayant une grille, et l'erreur de visée y est bien plus facile que sur
 * une ligne de tableau qu'on lit une par une.* Le geste irréversible reste où
 * le pointeur est posé, pas où il passe.
 *
 * ⚠️ **LE STOCK EST SUR LA VIGNETTE, PAS AU SURVOL.** C'est la seule
 * information qu'on vient chercher **en même temps** que l'aspect : « c'est
 * celui-là, et il en reste combien ? ». La cacher derrière un survol forcerait
 * à survoler chaque carte pour reconstituer ce que le tableau donne d'un coup.
 *
 * 🔴 ET LA PASTILLE DE STOCK BAS EST **TOUJOURS** VISIBLE, elle. Elle porte une
 * alerte : *une alerte qui n'apparaît qu'au survol n'alerte personne* — il faut
 * déjà avoir trouvé l'article pour la voir, alors qu'elle sert justement à le
 * faire trouver.
 */
const GalerieProduits = ({
  produits, onAjuster, onCommander, onModifier, onOuvrir,
}) => {
  const { t } = useLanguage();

  return (
    <Box
      sx={{
        display: 'grid',
        // ⚠️ `auto-fill` ET NON UN NOMBRE DE COLONNES PAR POINT DE RUPTURE : la
        // grille se remplit d'elle-même selon la largeur disponible, donc elle
        // suit aussi le repli de la barre latérale — qu'aucun `breakpoint` ne
        // connaît, puisqu'il mesure la fenêtre et non le conteneur.
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 2,
      }}
    >
      {produits.map((produit) => {
        // 🔴 `is_low_stock` VIENT DU SERVEUR — on ne RECOPIE pas la condition.
        // Ma première version écrivait `current_stock <= min_stock` ici, et la
        // garde `I1` l'a attrapée : le seuil est une règle MÉTIER, servie par
        // `Product.to_dict()`, et elle porte une subtilité qu'une recopie perd
        // — `min_stock = 0` ne signifie pas « tout est bas », il signifie
        // « pas de seuil ». *Une condition recopiée est une condition qui
        // divergera le jour où la règle changera d'un seul côté.*
        const bas = Boolean(produit.is_low_stock);

        return (
          <Card
            key={produit.id}
            onClick={() => onOuvrir && onOuvrir(produit)}
            sx={{
              position: 'relative',
              cursor: onOuvrir ? 'pointer' : 'default',
              // ⚠️ Le survol RÉVÈLE les actions, il ne les crée pas : elles
              // sont montées en permanence et changent d'opacité. Les monter
              // au survol ferait cligner la carte à chaque passage.
              '&:hover .actions-galerie': { opacity: 1 },
            }}
          >
            {produit.image_path ? (
              <CardMedia
                component="img"
                height="150"
                image={fileUrl(produit.image_path)}
                alt={produit.name}
                sx={{ objectFit: 'cover' }}
              />
            ) : (
              /* ⚠️ UN EMPLACEMENT DE MÊME HAUTEUR, pas un vide : sans lui, une
                 grille où la moitié des produits n'ont pas de photo se
                 disloque, et l'œil ne peut plus balayer une rangée. */
              <Box
                sx={{
                  height: 150,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  bgcolor: 'action.hover',
                }}
              >
                <PhotoCameraIcon color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  {t('products.noPhotoShort')}
                </Typography>
              </Box>
            )}

            {/* Les trois actions — révélées au survol, jamais la suppression. */}
            <Box
              className="actions-galerie"
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute', top: 4, insetInlineEnd: 4,
                display: 'flex', gap: 0.5,
                opacity: 0,
                transition: 'opacity 0.2s',
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 1,
                // ⚠️ VISIBLE EN PERMANENCE SUR UN ÉCRAN TACTILE : il n'y a pas
                // de survol au doigt. Sans cette règle, les trois actions
                // seraient inatteignables sur tablette — l'appareil le plus
                // probable dans un entrepôt.
                '@media (hover: none)': { opacity: 1 },
              }}
            >
              <Tooltip title={t('products.adjust')}>
                <IconButton size="small" onClick={() => onAjuster(produit)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('products.orderThisProduct')}>
                <IconButton size="small" color="primary"
                            onClick={() => onCommander(produit)}>
                  <ShoppingCartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.edit')}>
                <IconButton size="small" onClick={() => onModifier(produit)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ p: 1.5 }}>
              {/* ⚠️ `noWrap` + `title` : un nom long est coupé à l'écran mais
                  reste lisible au survol. Le laisser passer à la ligne ferait
                  varier la hauteur des cartes d'une rangée à l'autre. */}
              <Typography variant="subtitle2" noWrap title={produit.name}>
                {produit.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {produit.price != null
                    ? formatCurrency(produit.price, produit.currency || 'EUR')
                    : '—'}
                </Typography>
                {/* 🔴 LA COULEUR SUIT LE SEUIL, et le seuil est RAPPELÉ à côté
                    du stock — `8 / 20` se lit seul, `8` demande de savoir. */}
                <Typography
                  variant="body2"
                  sx={{ ml: 'auto', fontWeight: 600,
                        color: bas ? 'warning.main' : 'text.primary' }}
                >
                  {produit.current_stock}
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {' / '}{produit.min_stock}
                  </Box>
                </Typography>
              </Box>

              {bas && (
                <Chip size="small" color="warning" variant="outlined"
                      label={t('products.lowStock')} sx={{ mt: 1 }} />
              )}
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export default GalerieProduits;
